import { isObject } from './validators.js';


const uniqStore = {};

/**
 * Creates a generator that builds a unique id given a string and a counter
 * If a string is used more than once, return the previous generator rather than make a new one
 * @param  {String} name - string to namespace ids
 * @param  {Number} counter - the point at which to begin counting
 * @return {Function} a counter function that combines the name given and the counter to generate a unique id
 */
export function uniqueId(name = '', counter = 0) {
    if (!uniqStore[name]) {
        uniqStore[name] = () => `${name}${++counter}`;
    }
    return uniqStore[name];
}

/**
 *  isEqual - simple implementation of lodash isEqual - we make the assumption that all values are either
 *  primitives (value type), arrays (reference), or non-circular json objects - our typical use
 *  case will be making comparison of two non-circular json objects containing some combination of the above elements
 *
 *  @param {*} a - first item
 *  @param {*} b - second item
 *  @return {bool} - whether they are equivalent or not
 */
export function isEqual(a, b) {
    /* if a is an array, b must also be an array of the same length, with pairwise isEqual elements to a */
    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) {
            return false;
        }
        return a.every((elem, idx) => isEqual(elem, b[idx]), true);
    }
    /**
     *  if a is a non-null object (and not an array), b must also be a non-array non-null object with the same amount of keys
     *  as a, where the pairwise keys are isEqual
     */
    if (isObject(a)) {
        if (!isObject(b) || Array.isArray(b)) {
            return false;
        }
        const a_keys = Object.keys(a).sort();
        const b_keys = Object.keys(b).sort();
        return isEqual(a_keys, b_keys) && a_keys.every((key) => isEqual(a[key], b[key]));
    }
    /* since a is neither a non-null object nor an array, it must be a primitive value and so we can use === against b */
    return a === b;
}
